import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { toastError } from '@components/Toast';

import TargetAndAchievementGraph from '@features/crm/brands/components/TargetAndAchievementGraph';
import TotalStyleChart from '@features/crm/components/TotalStyleChart';
import { acceptFileType, currentStageColumns, getPercentageValue } from '@features/crm/utils/helpers';

import { fetchBrandProfileById } from '@store/actions/BrandProfileAction';
import { setCostingFilterField } from '@store/actions/CostingAction';
import { removeItemFromGlobalFilter, storeInitialItemInGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { setSampleRequestFilterField } from '@store/actions/SampleAction';

import ApiClient from '@lib/apiClient';

import { convertNumberToUSFormat, getNumberUnit, isCRMAdmin, isValidFile } from '@utils/Util';

import { BASE_URL, VALID_FILE_SIZE } from '@/constants/constant';

const BrandIntroduction = () => {
	const brandProfile = useSelector((store) => store.brandProfileStore);
	const [activitySummary, setActivitySummary] = useState({});
	const [orderSummary, setOrderSummary] = useState({});
	const [inputData, setInputData] = useState({});
	const [logoDocumentDTO, setLogoDocumentDTO] = useState(null);
	const [editableField, setEditableField] = useState(null);
	const dispatch = useDispatch();
	const { id } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		setInputData({
			name: brandProfile.name
		});
	}, [brandProfile]);

	const fetchActivitySummary = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.GET(`${BASE_URL}/brand/${id}/running-activity`, '', true);
			setActivitySummary(data);
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.message);
		}
	};

	const fetchOrderSummary = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.GET(
				`${BASE_URL}/order-dashboard/order-summary-by-brandId/${id}`,
				'',
				true
			);
			setOrderSummary(data);
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.message);
		}
	};

	useEffect(() => {
		fetchActivitySummary();
		fetchOrderSummary();
	}, []);

	const calculateMargin = () => {
		return (
			((orderSummary?.poAmountHasFactoryCost - orderSummary?.factoryCost) /
				orderSummary?.poAmountHasFactoryCost) *
			100
		);
	};

	const renderOrderOrOrders = (fieldName = 'noOfOrder') => {
		return orderSummary?.[fieldName] > 1 ? 'Orders' : 'Order';
	};

	useEffect(() => {
		if (logoDocumentDTO !== null) {
			let postData = {
				id,
				logoDocumentDTO
			};
			handleSave(postData);
		}
	}, [logoDocumentDTO]);

	const handleSave = async (postData) => {
		dispatch(openLoader());
		try {
			await ApiClient.PUT('updateBrandInfo', postData);
			dispatch(fetchBrandProfileById(id));
			dispatch(closeLoader());
		} catch {
			toastError('Update failed!');
			dispatch(closeLoader());
		}
	};

	const onFileUpload = (e) => {
		let file = e.target.files[0];
		if (isValidFile(file, 'image', VALID_FILE_SIZE)) {
			let data = {
				name: file.name,
				docMimeType: file.type,
				documentType: 'ACCESSORIES_DESIGN',
				base64Str: '',
				size: file.size
			};
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				data.base64Str = reader.result;
				setLogoDocumentDTO(data);
			};
			reader.onerror = function (error) {
				toastError(error);
			};
		}
	};

	const handleChange = (e) => {
		const { value } = e.target;
		setInputData((prev) => ({ ...prev, name: value }));
	};

	const handleBlur = async () => {
		let postData = {
			id,
			name: inputData.name
		};
		await handleSave(postData);
		setEditableField(null);
	};

	const renderBrandName = () => {
		if (editableField === 'brandName') {
			return (
				<input
					type="text"
					autoComplete="off"
					autoFocus={true}
					value={inputData.name || ''}
					onChange={handleChange}
					onBlur={handleBlur}
				/>
			);
		} else {
			return brandProfile.name;
		}
	};

	const getStatusWiseColor = (status) => {
		switch (status) {
			case 'ACTIVE':
				return 'green-3';
			case 'ARCHIVE':
				return 'yellow-1';
			case 'ON_BOARDED':
				return 'purple-1';
			case 'ON_BOARDING':
				return 'orange-1';
			case 'PIPELINE':
				return 'green-1';
			default:
				return 'green-3';
		}
	};

	const redirectUrl = (url) => {
		if (!/^https?:\/\//i.test(url)) {
			url = 'https://' + url;
		}
		return url;
	};

	const renderStatus = () => {
		let tmpCurrentStages = currentStageColumns;
		if (isCRMAdmin()) {
			tmpCurrentStages = [...currentStageColumns, { label: 'Pending', value: 'PENDING' }];
		}
		return tmpCurrentStages?.find((item) => item.value === brandProfile.status)?.label;
	};

	const onClickOrders = async () => {
		dispatch(removeItemFromGlobalFilter({ name: 'dateRange', identifier: 'dateRange' }));
		const initialState = {
			brandId: [
				{
					name: 'brandId',
					key: id,
					value: brandProfile.name,
					identifier: `brand_${id}`,
					checked: true
				}
			],
			orderStatus: [
				{
					name: 'orderStatus',
					key: 'PROFORMA_INVOICE',
					value: 'Pending - PI',
					identifier: `orderStage_PROFORMA_INVOICE`,
					checked: true
				},
				{
					name: 'orderStatus',
					key: 'SALES_CONTRACT',
					value: 'Pending - SC',
					identifier: `orderStage_SALES_CONTRACT`,
					checked: true
				}
			]
		};
		await dispatch(storeInitialItemInGlobalFilter(initialState));
		navigate('/orders/my-orders?active=ORDER_DASHBOARD');
	};

	const onClickQuotes = async () => {
		await dispatch(setCostingFilterField({ brand: { id, value: brandProfile.name } }));
		navigate('/costing/initial');
	};

	const onClickSample = async () => {
		await dispatch(setSampleRequestFilterField({ brand: { id, value: brandProfile.name } }));
		navigate('/sample/all');
	};

	return (
		<div className="brand-intro d-flex">
			<div className="left-half">
				<div className="brand-status d-flex">
					<div className="logo">
						{brandProfile.brandLogoDocResponse?.docUrl && (
							<img src={brandProfile.brandLogoDocResponse.docUrl} alt="logo" className="logo" />
						)}
						<div className="reupload-brand">
							<label
								htmlFor="reuploaBrand"
								className="drag-upload cursor-pointer mb-0 mr-2 pr-1"
							>
								<img
									src={`${process.env.PUBLIC_URL}/icons/upload-white-20.svg`}
									alt="upload"
									className="upload-icon"
								/>
								<p className="regular-10 text-white">Change Photo</p>
							</label>
							<input
								type="file"
								className="file-upload d-none"
								name="reuploaBrand"
								id="reuploaBrand"
								onClick={(e) => (e.target.value = null)}
								accept={acceptFileType('image')}
								onChange={(e) => onFileUpload(e)}
							/>
						</div>
					</div>
					<div>
						<p className="brand-name regular-18 gray_dark_01 weight-500 text-uppercase d-flex align-items-center">
							{renderBrandName()}
							<img
								onClick={() => setEditableField('brandName')}
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1 cursor-pointer"
							/>
						</p>
						<p>
							<span
								onClick={() => window.open(redirectUrl(brandProfile.website), '_blank')}
								className="regular-12 gray_dark_03 cursor-pointer"
							>
								{brandProfile.website}
							</span>
						</p>
						{brandProfile.group && (
							<span className="status yellow-2 mr-1">{brandProfile.group}</span>
						)}
						{brandProfile.type && <span className="status blue-1 mr-1">{brandProfile.type}</span>}
						{brandProfile?.status && (
							<span className={`status ${getStatusWiseColor(brandProfile.status)}`}>
								{renderStatus()}
							</span>
						)}
						{brandProfile?.locationResponseList?.[0]?.address && (
							<p className="regular-14 gray_dark_01 mt-3 d-flex align-items-start">
								<img
									src={`${process.env.PUBLIC_URL}/icons/map-pin.svg`}
									alt="map"
									className="mr-1"
								/>{' '}
								<span>
									{brandProfile?.locationResponseList?.[0]?.address},
									{brandProfile?.locationResponseList?.[0]?.countryResponse?.name}
								</span>
							</p>
						)}
					</div>
				</div>
				<div className="target-and-opportunity mt-5">
					<TargetAndAchievementGraph activitySummary={activitySummary} />

					<div className="opportunity">
						<p className="d-flex justify-content-between regular-14 mb-1">
							{activitySummary?.projectedAmount - activitySummary?.acheivedAmount > 0 && (
								<span className="blue">
									{getNumberUnit(
										activitySummary?.projectedAmount - activitySummary?.acheivedAmount
									)}
								</span>
							)}
						</p>
						<div className="progress progress-square">
							<div
								className="progress-bar"
								role="progressbar"
								style={{
									width: `${
										activitySummary?.projectedAmount - activitySummary?.acheivedAmount > 0
											? activitySummary?.projectedAmount -
												activitySummary?.acheivedAmount
											: 0
									}%`
								}}
								aria-valuenow={100}
								aria-valuemin={0}
								aria-valuemax={100}
							/>
						</div>
					</div>

					<div className="target-achivement-oppotunity-values">
						<ul>
							<li className="regular-12 gary_dark_01">
								<span className="indicator target" />
								Target{' '}
								{activitySummary?.projectedAmount && (
									<>(${getNumberUnit(activitySummary?.projectedAmount)})</>
								)}
							</li>
							<li className="regular-12 gary_dark_01">
								<span className="indicator achivement" />
								Achievement{' '}
								{activitySummary?.acheivedAmount && (
									<>(${getNumberUnit(activitySummary?.acheivedAmount)})</>
								)}
							</li>
							<li className="regular-12 gary_dark_01">
								<span className="indicator opportunity" />
								Opportunity&nbsp;
								{activitySummary?.projectedAmount - activitySummary?.acheivedAmount > 0 && (
									<>
										($
										{getNumberUnit(
											activitySummary?.projectedAmount - activitySummary?.acheivedAmount
										)}
										)
									</>
								)}
							</li>
						</ul>
					</div>
				</div>
				<div className="running-opportunities mt-4">
					<p className="regular-18 weight-500 gray_dark_01 mb-2">Running Activities</p>
					<div className="d-flex justify-content-between">
						<div
							onClick={() => (activitySummary?.orders ? onClickOrders : false)}
							className="cursor-pointer"
						>
							<p className="regular-18 weight-500 gray_dark_01 mb-1">
								{activitySummary?.orders
									? getNumberUnit(activitySummary?.orders, true)
									: 'N/A'}
							</p>
							<p className="regular-14 gray_dark_02">Orders</p>
						</div>
						<div
							onClick={() => (activitySummary?.quotes ? onClickQuotes : false)}
							className="cursor-pointer"
						>
							<p className="regular-18 weight-500 gray_dark_01 mb-1">
								{activitySummary?.quotes
									? getNumberUnit(activitySummary?.quotes, true)
									: 'N/A'}
							</p>
							<p className="regular-14 gray_dark_02">Quotes</p>
						</div>
						<div
							onClick={() => (activitySummary?.sample ? onClickSample : false)}
							className="cursor-pointer"
						>
							<p className="regular-18 weight-500 gray_dark_01 mb-1">
								{activitySummary?.sample
									? getNumberUnit(activitySummary?.sample, true)
									: 'N/A'}
							</p>
							<p className="regular-14 gray_dark_02">Sample</p>
						</div>
						<div>
							<p className="regular-18 weight-500 gray_dark_01 mb-1">
								{activitySummary?.collections
									? getNumberUnit(activitySummary?.collections, true)
									: 'N/A'}
							</p>
							<p className="regular-14 gray_dark_02">Collection</p>
						</div>
					</div>
				</div>
			</div>
			<div className="right-half">
				<div className="all-updates">
					<div className="single-update">
						<p className="regular-18 gray_dark_01 weight-500 mb-1">
							{orderSummary?.poAmount > 0 ? `$${getNumberUnit(orderSummary?.poAmount)}` : ''}
						</p>
						<p className="regular-14 gray_dark_02">
							Total PO Value{' '}
							{orderSummary?.noOfOrder > 0 && (
								<span className="regular-10">
									({orderSummary?.noOfOrder} {renderOrderOrOrders()} -{' '}
									{orderSummary?.noOfNitexStyle + orderSummary?.noOfBuyerStyle} Styles)
								</span>
							)}
						</p>
					</div>
					<div className="single-update">
						{orderSummary?.quantity > 0 && (
							<p className="regular-18 gray_dark_01 weight-500 mb-1">
								{convertNumberToUSFormat(orderSummary.quantity, true)} pcs
							</p>
						)}
						<p className="regular-14 gray_dark_02">
							Total QTY{' '}
							{orderSummary?.noOfOrder > 0 && (
								<span className="regular-10">
									({orderSummary?.noOfOrder} {renderOrderOrOrders()} -{' '}
									{orderSummary?.noOfNitexStyle + orderSummary?.noOfBuyerStyle} Styles)
								</span>
							)}
						</p>
					</div>
					<div className="single-update">
						<p className="regular-18 gray_dark_01 weight-500 mb-1">
							{orderSummary?.poAmountHasFactoryCost > 0
								? `$${getNumberUnit(
										orderSummary?.poAmountHasFactoryCost -
											(orderSummary?.factoryCost || 0)
									)}`
								: ''}{' '}
							<span className="secondary">
								{isNaN(calculateMargin()) ? '' : `(${calculateMargin().toFixed(2)}%)`}
							</span>
						</p>
						<p className="regular-14 gray_dark_02">
							Expected margin ({orderSummary?.noOfStyleHasFactoryCost} Styles)
						</p>
					</div>
					<div className="single-update">
						<p className="regular-18 gray_dark_01 weight-500 mb-1">
							{orderSummary?.factoryCost ? `$${getNumberUnit(orderSummary.factoryCost)}` : ''}
						</p>
						<p className="regular-14 gray_dark_02">
							SC value ({orderSummary?.noOfStyleHasFactoryCost} Styles)
						</p>
					</div>
					<div className="single-update">
						<p className="regular-18 gray_dark_01 weight-500 mb-1">
							{brandProfile?.annualProduction > 0
								? `$${getNumberUnit(brandProfile?.annualProduction)}`
								: ''}
						</p>
						<p className="regular-14 gray_dark_02">Annual production req</p>
					</div>
					<div className="single-update">
						<div className="d-flex align-items-center">
							<div className="chart">
								<TotalStyleChart orderSummary={orderSummary} />
							</div>
							<div className="chart-info">
								<p className="regular-14 gray_dark_02">
									<span className="c-indicator nitex" />
									Nitex design{' '}
									<span className="semibold-14 ml-1">
										{orderSummary?.noOfNitexStyle} (
										{getPercentageValue(
											orderSummary.noOfNitexStyle,
											orderSummary.noOfBuyerStyle
										)}
										%)
									</span>{' '}
									{orderSummary?.nitexStyleAmount
										? ` - $${getNumberUnit(orderSummary?.nitexStyleAmount)}`
										: ''}
								</p>
								<p className="regular-14 gray_dark_02">
									<span className="c-indicator buyer" />
									Buyer design{' '}
									<span className="semibold-14 ml-1">
										{orderSummary?.noOfBuyerStyle} (
										{getPercentageValue(
											orderSummary.noOfBuyerStyle,
											orderSummary.noOfNitexStyle
										)}
										%)
									</span>{' '}
									{orderSummary?.buyerStyleAmount
										? ` - $${getNumberUnit(orderSummary?.buyerStyleAmount)}`
										: ''}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BrandIntroduction;
